import React, { useEffect, useState } from 'react';
import {
  Table,
  Drawer,
  Descriptions,
  Tag,
  message,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Space,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  WinningRecord,
  WinningRecordParams,
  getWinningRecordList,
  getPrizeDetail,
  updatePrizeStatusToReceived,
  updateLogisticsInfo,
  getPrizeStatistics,
} from '@/api/lottoActivity';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

const WinningRecordList: React.FC = () => {
  const getPrizeTypeName = (type: number): string => {
    switch (type) {
      case 1:
        return '实物';
      case 2:
        return '虚拟商品';
      case 3:
        return '门票';
      case 4:
        return '卡券';
      case 5:
        return '谢谢参与';
      case 6:
        return '积分';
      default:
        return '未知';
    }
  };
  const [form] = Form.useForm();
  const [logisticsForm] = Form.useForm();
  const [prizeStats, setPrizeStats] = useState({
    issued: 0,
    received: 0,
    unclaimed: 0,
  });
  const [searchParams] = useSearchParams();
  const activityId = Number(searchParams.get('activityId'));

  const [data, setData] = useState<WinningRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<WinningRecord | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const displayOrDash = (val?: string | null) => (val ? val : '-');
  const handleShowDetail = async (id: number) => {
    logisticsForm.resetFields();
    const res = await getPrizeDetail(id);
    if (res.success && res.data) {
      setDetail(res.data);
      setDrawerVisible(true);
    } else {
      message.error('获取详情失败');
    }
  };

  const fetchPrizeStatistics = async () => {
    if (!activityId) return;
    const res = await getPrizeStatistics(activityId);
    if (res.success && res.data) {
      setPrizeStats(res.data);
    }
  };

  const buildParams = (): WinningRecordParams => {
    const values = form.getFieldsValue();
    return {
      currentPage: page,
      pageSize,
      activityId,
      keyword: values.keyword || undefined,
      prizeStatus: values.prizeStatus,
      prizeType: values.prizeType,
      startTime: values.dateRange?.[0]
        ? dayjs(values.dateRange[0]).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      endTime: values.dateRange?.[1]
        ? dayjs(values.dateRange[1]).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
    };
  };

  const fetchData = async () => {
    if (!activityId) {
      message.error('缺少活动ID，无法加载中奖记录');
      return;
    }
    const params = buildParams();
    setLoading(true);
    const res = await getWinningRecordList(params);
    if (res?.success && res.data) {
      setData(res.data.records || []);
      setTotal(res.data.total || 0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    fetchPrizeStatistics();
  }, [page, pageSize, activityId]);

  const handleExport = async () => {
    if (!activityId) return;
    const params: WinningRecordParams = {
      ...form.getFieldsValue(),
      activityId,
      currentPage: 1,
      pageSize: 99999, // 导出全部筛选后的记录
    };
    const res = await getWinningRecordList(params);
    if (!res.success || !res.data || !res.data.records?.length) {
      message.warning('暂无可导出的数据');
      return;
    }
    const headers = [
      '中奖时间',
      '奖品名称',
      '奖品类型',
      '奖品状态',
      '获奖用户ID',
      '获奖用户昵称',
      '获奖用户手机号',
    ];
    const rows = res.data.records.map((item) => [
      item.grantTime ? dayjs(item.grantTime).format('YYYY-MM-DD HH:mm:ss') : '',
      item.prizeName,
      getPrizeTypeName(item.prizeType),
      item.prizeStatus === 1
        ? '待领取'
        : item.prizeStatus === 2
          ? '已领取'
          : item.prizeStatus === 3
            ? '待发货'
            : item.prizeStatus === 4
              ? '已过期'
              : '未知',
      item.userId ? `="${item.userId}"` : '',
      item.userName || '',
      item.phoneNumber ? `="${item.phoneNumber}"` : '',
    ]);
    const csvContent =
      '\uFEFF' +
      [headers, ...rows]
        .map((row) =>
          row.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',')
        )
        .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `中奖记录_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const columns: ColumnsType<WinningRecord> = [
    {
      title: '中奖时间',
      dataIndex: 'grantTime',
      render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '奖品名称',
      dataIndex: 'prizeName',
      render: (_, record) => (
        <a
          style={{ color: '#1890ff' }}
          onClick={() => handleShowDetail(record.id)}
        >
          {record.prizeName}
        </a>
      ),
    },
    {
      title: '奖品类型',
      dataIndex: 'prizeType',
      render: (val) => getPrizeTypeName(val),
    },
    {
      title: '奖品状态',
      dataIndex: 'prizeStatus',
      render: (val) => {
        switch (val) {
          case 1:
            return <Tag color="blue">待领取</Tag>;
          case 2:
            return <Tag color="green">已领取</Tag>;
          case 3:
            return <Tag color="orange">待发货</Tag>;
          case 4:
            return <Tag color="red">已过期</Tag>;
          default:
            return <Tag>未知</Tag>;
        }
      },
    },
    {
      title: '获奖用户ID',
      dataIndex: 'userId',
    },
    {
      title: '获奖用户信息',
      render: (_, record) => (
        <>
          {record.userName || ''}
          <br />
          {record.phoneNumber || ''}
        </>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: '16px', marginBottom: 20 }}>
        <div
          style={{
            flex: 1,
            background: '#f0f5ff',
            padding: 16,
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {prizeStats.issued}
          </div>
          <div>已发放奖品数量</div>
        </div>
        <div
          style={{
            flex: 1,
            background: '#f6ffed',
            padding: 16,
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {prizeStats.received}
          </div>
          <div>已领取奖品数量</div>
        </div>
        <div
          style={{
            flex: 1,
            background: '#fff7e6',
            padding: 16,
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {prizeStats.unclaimed}
          </div>
          <div>未领取奖品数量</div>
        </div>
      </div>
      <Form
        form={form}
        layout="inline"
        onFinish={fetchData}
        style={{ marginBottom: 20 }}
      >
        <Form.Item name="dateRange">
          <DatePicker.RangePicker />
        </Form.Item>
        <Form.Item name="keyword">
          <Input placeholder="奖品名称/用户ID/用户手机号/昵称" />
        </Form.Item>
        <Form.Item name="prizeStatus">
          <Select placeholder="奖品状态" style={{ width: 120 }}>
            <Select.Option value={1}>待领取</Select.Option>
            <Select.Option value={2}>已领取</Select.Option>
            <Select.Option value={3}>待发货</Select.Option>
            <Select.Option value={4}>已过期</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="prizeType">
          <Select placeholder="奖品类型" style={{ width: 120 }}>
            <Select.Option value={1}>实物</Select.Option>
            <Select.Option value={2}>虚拟商品</Select.Option>
            <Select.Option value={3}>门票</Select.Option>
            <Select.Option value={4}>卡券</Select.Option>
            {/* <Select.Option value={6}>积分</Select.Option> */}
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
            <Button
              onClick={() => {
                form.resetFields();
                setPage(1);
                setPageSize(10);
                fetchData();
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>所有记录，共 {total} 条</div>
        <Button type="default" onClick={handleExport}>
          导出兑奖信息
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          total,
          current: page,
          pageSize,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        style={{ flex: 1 }}
      />
      <Drawer
        title="记录详情"
        width={520}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        maskClosable={false}
      >
        {detail && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="奖品名称">
                {detail.prizeName}
              </Descriptions.Item>
              <Descriptions.Item label="奖品类型">
                {getPrizeTypeName(detail.prizeType)}
              </Descriptions.Item>
              <Descriptions.Item label="奖品状态">
                <Space>
                  {
                    {
                      1: <Tag color="blue">待领取</Tag>,
                      2: <Tag color="green">已领取</Tag>,
                      3: <Tag color="orange">待发货</Tag>,
                      4: <Tag color="default">已过期</Tag>,
                    }[detail.prizeStatus]
                  }
                  {detail.prizeStatus === 1 && (
                    <Button
                      type="link"
                      onClick={async () => {
                        const res = await updatePrizeStatusToReceived(
                          detail.id
                        );
                        if (res.success) {
                          message.success('已标记为已领取');
                          fetchData();
                          handleShowDetail(detail.id);
                        } else {
                          message.error('更新失败');
                        }
                      }}
                    >
                      标记为已领取
                    </Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="中奖时间">
                {dayjs(detail.grantTime).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="中奖ID">{detail.id}</Descriptions.Item>
              <Descriptions.Item label="用户ID">
                {displayOrDash(detail.userId)}
              </Descriptions.Item>
              <Descriptions.Item label="用户名">
                {displayOrDash(detail.userName)}
              </Descriptions.Item>
              <Descriptions.Item label="手机号">
                {displayOrDash(detail.phoneNumber)}
              </Descriptions.Item>
              <Descriptions.Item label="收货地址">
                {detail.receiveAddress ? (
                  <div>
                    <div>
                      {displayOrDash(detail.receiveAddress.fullName)}（
                      {displayOrDash(detail.receiveAddress.fullNameInFurigana)}
                      ）
                    </div>
                    <div>
                      〒{displayOrDash(detail.receiveAddress.postalCode)}
                    </div>
                    <div>
                      {displayOrDash(detail.receiveAddress.baseAddress)}{' '}
                      {displayOrDash(detail.receiveAddress.detailAddress)}
                    </div>
                    <div>
                      {displayOrDash(detail.receiveAddress.phoneNumber)}
                    </div>
                    <div>{displayOrDash(detail.receiveAddress.email)}</div>
                  </div>
                ) : (
                  '-'
                )}
              </Descriptions.Item>

              <Descriptions.Item label="物流公司">
                {detail.logisticsCompany ? (
                  <span>{detail.logisticsCompany}</span>
                ) : (
                  <Form form={logisticsForm}>
                    <Form.Item
                      name="logisticsCompany"
                      initialValue={detail.logisticsCompany}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="请输入物流公司"
                        disabled={detail.prizeType === 2}
                      />
                    </Form.Item>
                  </Form>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="运单号">
                {detail.waybillNumber ? (
                  <span>
                    {detail.waybillNumber}{' '}
                    <CopyOutlined
                      onClick={() => {
                        navigator.clipboard.writeText(
                          detail.waybillNumber || ''
                        );
                        message.success('运单号已复制');
                      }}
                      style={{ cursor: 'pointer', marginLeft: 8 }}
                    />
                  </span>
                ) : (
                  <Form form={logisticsForm}>
                    <Form.Item
                      name="waybillNumber"
                      initialValue={detail.waybillNumber}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="请输入运单号"
                        disabled={detail.prizeType === 2}
                      />
                    </Form.Item>
                  </Form>
                )}
              </Descriptions.Item>

              <Descriptions.Item label=" ">
                <Space>
                  <Button
                    type="primary"
                    disabled={detail.prizeType === 2}
                    onClick={async () => {
                      const values = await logisticsForm.validateFields();
                      const res = await updateLogisticsInfo({
                        id: detail.id,
                        ...values,
                      });
                      if (res.success) {
                        message.success('物流信息已更新');
                        fetchData();
                        handleShowDetail(detail.id);
                      } else {
                        message.error('更新失败');
                      }
                    }}
                  >
                    保存物流信息
                  </Button>
                  <Button onClick={() => logisticsForm.resetFields()}>
                    重置
                  </Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default WinningRecordList;
